/**
 * The command queue: the UI's only write path into the sim
 * (docs/13-architecture.md — UI → sim via a serialized command queue, never a
 * call). Like the snapshot ring, it has two backings behind one interface.
 *
 * - **`sab`** — a fixed mailbox of slots in shared memory. The main thread
 *   writes a batch and bumps `head` with `Atomics`; the worker drains up to
 *   `head` at its next tick boundary and bumps `tail`. A full mailbox drops the
 *   batch and increments a counter, because a silently swallowed player action
 *   is the worst outcome and an unbounded queue is the second worst.
 * - **`copy`** — `postMessage`. Same payload, same tick-boundary application.
 *
 * The payload is JSON of the generated `CampaignCommand` union (M1.1; each
 * entry names the clock it belongs to). Commands are
 * player actions, not frames, so their traffic is a rounding error next to the
 * snapshot ring's — and sharing one schema with Rust is worth more here than
 * bytes. If B0.6's boundary budget ever disagrees, that is the moment to pack
 * them, with the measurement in hand.
 */

import {
  CMD_CONTINUES,
  CMD_DROPPED,
  CMD_HEAD,
  CMD_SLOT_BYTES,
  CMD_SLOTS,
  CMD_TAIL,
  CMD_WORDS,
  type CampaignCommand,
} from './protocol';

/** Payload bytes a slot can carry, after its length word. */
const CHUNK_BYTES = CMD_SLOT_BYTES - 4;

/** What the UI calls. Neither side knows which transport it got. */
export interface CommandSink {
  send(commands: CampaignCommand[]): void;
  /** Batches dropped for want of mailbox room. Zero, or a HUD warning. */
  dropped(): number;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** Main-thread writer for the shared mailbox. */
export class SabCommandSink implements CommandSink {
  private readonly control: Int32Array;
  private readonly data: Uint8Array;
  private readonly lengths: Uint32Array;

  constructor(control: SharedArrayBuffer, data: SharedArrayBuffer) {
    this.control = new Int32Array(control, 0, CMD_WORDS);
    this.data = new Uint8Array(data);
    this.lengths = new Uint32Array(data);
  }

  /**
   * Write a batch as one or more slots. A batch bigger than a slot — a layout
   * import is one — is chunked across consecutive slots and flagged, so it
   * arrives whole and in order rather than being dropped for size.
   */
  send(commands: CampaignCommand[]): void {
    if (commands.length === 0) return;
    const payload = encoder.encode(JSON.stringify(commands));
    const chunks = Math.max(1, Math.ceil(payload.byteLength / CHUNK_BYTES));
    let head = Atomics.load(this.control, CMD_HEAD);
    const tail = Atomics.load(this.control, CMD_TAIL);
    // All or nothing: half a batch in the mailbox would be worse than none.
    if (chunks > CMD_SLOTS || head - tail > CMD_SLOTS - chunks) {
      Atomics.add(this.control, CMD_DROPPED, 1);
      return;
    }
    for (let i = 0; i < chunks; i += 1) {
      const from = i * CHUNK_BYTES;
      const slice = payload.subarray(from, from + CHUNK_BYTES);
      const base = (head % CMD_SLOTS) * CMD_SLOT_BYTES;
      this.data.set(slice, base + 4);
      const more = i + 1 < chunks ? CMD_CONTINUES : 0;
      this.lengths[base / 4] = slice.byteLength | more;
      head += 1;
    }
    // The head moves once, after every chunk is written: a drain that sees it
    // sees the whole batch.
    Atomics.store(this.control, CMD_HEAD, head);
  }

  dropped(): number {
    return Atomics.load(this.control, CMD_DROPPED);
  }
}

/** Worker-side reader for the shared mailbox. */
export class SabCommandDrain {
  private readonly control: Int32Array;
  private readonly data: Uint8Array;
  private readonly lengths: Uint32Array;

  constructor(control: SharedArrayBuffer, data: SharedArrayBuffer) {
    this.control = new Int32Array(control, 0, CMD_WORDS);
    this.data = new Uint8Array(data);
    this.lengths = new Uint32Array(data);
  }

  /**
   * Every batch written since the last drain, as JSON strings, with chunked
   * batches reassembled.
   */
  drain(): string[] {
    const head = Atomics.load(this.control, CMD_HEAD);
    let tail = Atomics.load(this.control, CMD_TAIL);
    const batches: string[] = [];
    let carry: Uint8Array[] = [];
    while (tail < head) {
      const base = (tail % CMD_SLOTS) * CMD_SLOT_BYTES;
      const word = this.lengths[base / 4] ?? 0;
      const length = word & ~CMD_CONTINUES;
      const continues = (word & CMD_CONTINUES) !== 0;
      if (length > 0) {
        // Copied out of shared memory before decoding: `TextDecoder` refuses a
        // view backed by a `SharedArrayBuffer` outright, and a throw here would
        // take the worker's whole pump down with it.
        carry.push(new Uint8Array(this.data.subarray(base + 4, base + 4 + length)));
      }
      if (!continues && carry.length > 0) {
        batches.push(decoder.decode(join(carry)));
        carry = [];
      }
      tail += 1;
    }
    Atomics.store(this.control, CMD_TAIL, tail);
    return batches;
  }
}

/** Concatenate a chunked batch. One copy, on a path that runs on imports. */
function join(chunks: Uint8Array[]): Uint8Array {
  if (chunks.length === 1) return chunks[0]!;
  const total = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
  const out = new Uint8Array(total);
  let at = 0;
  for (const chunk of chunks) {
    out.set(chunk, at);
    at += chunk.byteLength;
  }
  return out;
}

/** Main-thread writer for the copy transport. */
export class PostCommandSink implements CommandSink {
  constructor(private readonly worker: Worker) {}

  send(commands: CampaignCommand[]): void {
    if (commands.length === 0) return;
    this.worker.postMessage({ type: 'commands', json: JSON.stringify(commands) });
  }

  dropped(): number {
    // A structured-clone queue has no fixed depth to overflow; it grows with
    // the event loop instead. Reported as zero so the HUD reads the same shape
    // on both paths — the number that matters here is latency, not loss.
    return 0;
  }
}
